// Helpers for the commitment "by when" field.
//
// Constraint per ManCo policy: deadlines can only be set within a forward
// 30-day window from today. The thinking is to force short-cycle re-commits
// instead of "TBC" rolling indefinitely. If something genuinely needs longer,
// the team recommits in 30 days with what's changed.

/** ISO `YYYY-MM-DD` for today (local-tz wall clock). */
export function isoToday(): string {
  return toIso(new Date());
}

/** ISO `YYYY-MM-DD` `days` after today. */
export function isoInDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toIso(d);
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** The forward 30-day window. Pass to `<input type="date" min max>`. */
export const DUE_BY_MIN_OFFSET_DAYS = 0;
export const DUE_BY_MAX_OFFSET_DAYS = 30;

/** Days from today until `iso` (positive = future, negative = past). */
export function daysUntil(iso: string): number {
  if (!iso) return 0;
  const target = new Date(iso + "T00:00:00");
  const today = new Date(isoToday() + "T00:00:00");
  return Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

/** "24 Jun" style — short, calendar-shaped. */
export function fmtDueBy(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short" })}`;
}

/**
 * Color a due-by chip by urgency:
 *   overdue (past) → coral
 *   ≤7 days        → amber
 *   ≤30 days       → ink (default)
 */
export function dueByTone(iso: string): "overdue" | "soon" | "default" {
  if (!iso) return "default";
  const d = daysUntil(iso);
  if (d < 0) return "overdue";
  if (d <= 7) return "soon";
  return "default";
}
